<template>
  <div>
    <b-row>
      <b-col>
        <draggable
          v-model="driversList"
          v-bind="options"
          :move="onMove"
          ref="draggable"
          class="d-inline"
          :disabled="false"
          draggable=".driver-item"
        >
          <div v-for="(element, index) in driversList" class="driver-item d-inline-block mb-2" :key="index">
            <div class="p-0 mr-2 d-inline-block font-weight-bold rounded selected-drivers" :style="setDriverStyle()">
              <div class="text-center d-inline-block indicator-default-style" :style="setIndicatorNumberStyle()">
                {{ index + 1 }}
              </div>
              <span class="ml-2 mr-2">
                {{ getDriverCode(element.driver) }}
              </span>

              <editable-badge
                :limit="100"
                :badge-id="index"
                :variant="'secondary'"
                :editable="false"
                :badge-value="$n(element.weight, 'percent')"
                :badge-class="['mr-2', 'align-baseline', 'driver-editable-badge']"
              >
                <span>{{ $n(element.weight, 'percent') }}</span>
              </editable-badge>

              <span class="ml-1 mr-2 pointer" @click="removeDriver(element)">
                <i class="fa fa-times" aria-hidden="true"></i>
              </span>
            </div>
          </div>

          <div
            v-if="driverFamiliesList.length > driversList.length"
            slot="footer"
            role="group"
            key="footer"
            :empty-insert-threshold="500"
            class="driver-item d-inline-block"
          >
            <div class="dashed selected-drivers p-0 rounded font-weight-bold mb-2 mr-2 d-inline-block">
              <div class="text-center d-inline-block driver-number">1</div>
            </div>
          </div>
        </draggable>
      </b-col>

      <b-col cols="auto" align-self="center">
        <slot></slot>
      </b-col>
    </b-row>

    <!-- separator -->
    <b-row no-gutters>
      <b-col>
        <div class="border-bottom mt-2 mb-3"></div>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <draggable v-model="driverFamiliesList" v-bind="options" :move="onMove">
          <div
            v-for="(element, index) in driverFamiliesList"
            class="driver-item d-inline-block mb-2 mr-2"
            :key="index"
            :class="draggableDriversClass(element.driver)"
            :style="setDriverStyle()"
            @dblclick="addDriver(element)"
          >
            <div class="ml-2 mr-2">
              {{ getDriverCode(element.driver) }}
            </div>
          </div>
        </draggable>
      </b-col>

      <b-col cols="auto" align-self="center">
        <template>
          <b-button
            variant="link"
            class="clear-all"
            @click="removeAll"
            :disabled="driversList && driversList.length === 0"
          >
            {{ $t('efficiencyTestDetails.resetSorting') }}
            <i class="fa fa-trash small trash-icon"></i>
          </b-button>
        </template>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import EditableBadge from '../EditableFields/EditableBadge.vue'

export default {
  components: {
    draggable,
    EditableBadge,
  },

  props: {
    drivers: {
      type: Array,
      required: true,
    },
    driverFamiliesList: {
      type: Array,
      required: true,
    },
    driversLimit: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      options: {
        animation: 0,
        disabled: false,
        group: { name: 'drivers', pull: 'clone' },
      },
    }
  },

  computed: {
    driversList: {
      get() {
        return this.drivers
      },
      set(newValue) {
        this.updateDriversListSorting(newValue)
      },
    },

    isDriversLimitReached: {
      get() {
        return Array.isArray(this.driversList) && this.driversList.length >= this.driversLimit
      },
    },
  },

  methods: {
    getDriverCode(item) {
      return this.$t('drivers.categories.' + item.toUpperCase())
    },

    setDriverStyle() {
      return 'color: var(--gray8); border: 1px solid var(--gray8)'
    },

    setIndicatorNumberStyle() {
      return 'background-color: var(--gray4); border-right: 1px solid var(--gray10);'
    },

    onMove({ relatedContext, draggedContext, dragged }) {
      const draggedElement = draggedContext.element
      if (dragged.classList.contains('draggable-indicators-disabled')) {
        return false
      }
      if (relatedContext.component !== this.$refs.draggable) {
        return false
      }
      if (this.isDriversLimitReached) {
        return false
      }
      const driverAlreadyAdded = this.driversList.some((item) => item.driver === draggedElement)
      return !driverAlreadyAdded
    },

    draggableDriversClass(item) {
      return [
        'draggable-indicators',
        Array.isArray(this.driversList) &&
        this.driversList.length > 0 &&
        this.driversList.some((k) => k.driver === item)
          ? 'draggable-indicators-disabled'
          : '',
      ]
    },

    removeAll() {
      this.$emit('on-reset-sorting')
    },

    updateDriversListSorting(value) {
      this.$emit('on-update-sorting', value)
    },

    addDriver(value) {
      this.$emit('on-move-driver', value)
    },

    removeDriver(value) {
      this.$emit('on-remove-model-driver', value)
    },
  },
}
</script>

<style lang="scss" scoped>
.draggable-indicators-disabled {
  opacity: 0.5;
}

.indicator-default-style {
  opacity: 0.5;
  color: #000;
  padding: 3px 4px;
}

.driver-number {
  padding: 3px 4px;
}

.trash-icon {
  font-size: 10px;
}

.driver-editable-badge {
  margin-bottom: 4px !important;
}

.text-orange {
  color: #ffa500 !important;
}

.dashed {
  border: 1px dashed rgb(219, 227, 237);
  color: transparent;
}

.clear-all {
  font-size: 0.8rem !important;
  text-decoration: none !important;
}

.alert-icon-padding {
  padding-top: 0.2rem;
}

.selected-drivers {
  cursor: move;
  padding: 5px 15px;
  min-height: 25px;
  min-width: 100px;
}

.draggable-indicators {
  padding: 3px 15px;
  cursor: move;
  border-radius: 4px;
  display: inline-block;
  font-weight: bold;
}
</style>
